import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Package, User, MapPin, CreditCard, Calendar, Mail, Phone, Download } from 'lucide-react';
import { downloadInvoice } from '../../lib/invoiceUtils';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function AdminOrderDetails() {
    const { orderId } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API}/orders/${orderId}`, {
                    headers: {
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                    },
                    credentials: 'include'
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch order details');
                }

                const data = await response.json();
                setOrder(data);
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        };

        if (orderId) {
            fetchOrderDetails();
        }
    }, [orderId]);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(price || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getImageUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        return `${BACKEND_URL}${url}`;
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered': return 'bg-[#e2f5e9] text-[#22a05b]';
            case 'shipped': return 'bg-blue-100 text-blue-700';
            case 'confirmed': return 'bg-purple-100 text-purple-700';
            case 'cancelled': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="text-center py-12">
                <h2 className="text-2xl font-semibold mb-2 text-gray-800">Order Not Found</h2>
                <p className="text-gray-500 mb-6">The order you are looking for does not exist.</p>
                <Link to="/admin/orders" className="text-blue-600 hover:underline flex items-center justify-center gap-2">
                    <ArrowLeft className="w-4 h-4" /> Back to Orders
                </Link>
            </div>
        );
    }

    const updateOrderStatus = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API}/orders/${orderId}/status?status=${order.status}`, {
                method: 'PUT',
                headers: {
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                credentials: 'include'
            });

            if (!response.ok) throw new Error('Failed to update status');
            toast.success('Order status updated');
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 max-w-[1200px] mx-auto p-4 md:p-6 bg-[#f4f7f6] min-h-[calc(100vh-80px)]" data-testid="admin-order-details">
            {/* Header / Breadcrumb */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-2 pb-4 border-b border-gray-200">
                <div className="flex flex-col gap-1">
                    <h1 className="font-semibold text-[28px] text-gray-800 leading-none">Order Details</h1>
                    <div className="text-sm text-gray-500 flex items-center gap-2 mt-2">
                        <Link to="/admin" className="hover:text-primary transition-colors">Dashboard</Link>
                        <span>/</span>
                        <Link to="/admin/orders" className="hover:text-primary transition-colors">Orders</Link>
                        <span>/</span>
                        <span className="text-blue-600">Order #{order.order_id}</span>
                    </div>
                </div>
                <button
                    onClick={() => downloadInvoice(order)}
                    className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg transition-all shadow-sm font-medium text-sm"
                >
                    <Download className="w-4 h-4" />
                    Download Invoice
                </button>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Left Column: Order Items */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="font-semibold text-[17px] mb-6 text-gray-800">Order Items</h3>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b-2 border-gray-100">
                                        <th className="pb-3 px-2 font-semibold text-gray-800 text-[15px]">Product</th>
                                        <th className="pb-3 px-2 font-semibold text-gray-800 text-[15px]">SKU</th>
                                        <th className="pb-3 px-2 font-semibold text-gray-800 text-[15px]">Quantity</th>
                                        <th className="pb-3 px-2 font-semibold text-gray-800 text-[15px]">Price</th>
                                        <th className="pb-3 px-2 font-semibold text-gray-800 text-[15px] text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.items?.map((item, index) => (
                                        <tr key={index} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition-colors">
                                            <td className="py-5 px-2 max-w-[320px]">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-16 h-16 bg-gray-50 rounded-lg shrink-0 overflow-hidden flex items-center justify-center border border-gray-100">
                                                        {getImageUrl(item.image) ? (
                                                            <img src={getImageUrl(item.image)} alt={item.name} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <Package className="w-6 h-6 text-gray-400" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-800 text-[15px] leading-snug break-words">{item.name}</p>
                                                        <p className="text-sm text-gray-500 mt-1 line-clamp-2 leading-relaxed break-words">{(item.description || item.name).substring(0, 120)}{item.description?.length > 120 ? '...' : ''}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-5 px-2 text-[15px] text-gray-600 align-top">
                                                {item.product_id?.substring(0, 12).toUpperCase() || 'N/A'}
                                            </td>
                                            <td className="py-5 px-2 text-[15px] text-gray-600 align-top">
                                                {item.quantity}
                                            </td>
                                            <td className="py-5 px-2 text-[15px] text-gray-600 align-top whitespace-nowrap">
                                                {formatPrice(item.price)}
                                            </td>
                                            <td className="py-5 px-2 text-[15px] font-bold text-gray-800 text-right align-top whitespace-nowrap">
                                                {formatPrice(item.price * item.quantity)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Customer and Shipping Details */}
                    <div className="grid md:grid-cols-2 gap-6 mt-6">
                        {/* Customer Info */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                            <h3 className="font-semibold text-[17px] mb-4 text-gray-800">Customer Details</h3>
                            <div className="space-y-3 text-[15px]">
                                <p className="flex items-center gap-3">
                                    <User className="w-4 h-4 text-gray-400 shrink-0" />
                                    <span className="font-medium text-gray-800 break-words">{order.customer?.name || order.user_name || 'N/A'}</span>
                                </p>
                                <p className="flex items-center gap-3">
                                    <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                                    <a href={`mailto:${order.customer?.email || order.user_email}`} className="text-blue-600 hover:underline break-all">{order.customer?.email || order.user_email || 'N/A'}</a>
                                </p>
                                {(order.customer?.phone || order.user_phone || order.shipping_address?.phone) && (
                                    <p className="flex items-center gap-3">
                                        <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                                        <span className="text-gray-600 break-words">{order.customer?.phone || order.user_phone || order.shipping_address?.phone}</span>
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Shipping details */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                            <h3 className="font-semibold text-[17px] mb-4 text-gray-800">Shipping Address</h3>
                            {order.shipping_address ? (
                                <div className="space-y-2 text-[15px] text-gray-600">
                                    <p className="font-medium text-gray-800 flex items-start gap-3">
                                        <MapPin className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                                        <span>{order.shipping_address.full_name || order.shipping_address.fullName}</span>
                                    </p>
                                    <div className="pl-7 space-y-1">
                                        <p>{order.shipping_address.address_line1 || order.shipping_address.streetAddress}</p>
                                        {(order.shipping_address.address_line2 || order.shipping_address.apartment) && <p>{order.shipping_address.address_line2 || order.shipping_address.apartment}</p>}
                                        <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.pincode}</p>
                                        <p className="mt-3 pt-3 border-t border-gray-100"><span className="font-medium text-gray-700">Phone:</span> {order.shipping_address.phone}</p>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-gray-500">No shipping address provided for this order.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                    {/* Order Information */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="font-semibold text-[17px] mb-5 text-gray-800">Order Information</h3>
                        <div className="space-y-4 text-[15px]">
                            <div className="flex justify-start gap-2 items-start">
                                <span className="font-semibold text-gray-700 w-32 shrink-0">Order Number:</span>
                                <span className="text-gray-600 break-all">{order.order_id}</span>
                            </div>
                            <div className="flex justify-start gap-2 items-start">
                                <span className="font-semibold text-gray-700 w-32 shrink-0">Date:</span>
                                <span className="text-gray-600">{formatDate(order.created_at)}</span>
                            </div>
                            <div className="flex justify-start gap-2 items-center">
                                <span className="font-semibold text-gray-700 w-32 shrink-0">Status:</span>
                                <Badge className={`${getStatusColor(order.status)} px-3 py-1 font-medium capitalize rounded-full text-[13px] border-none`}>
                                    {order.status}
                                </Badge>
                            </div>
                            <div className="flex justify-start gap-2 items-center">
                                <span className="font-semibold text-gray-700 w-32 shrink-0">Payment Status:</span>
                                <Badge className={`${order.payment_status === 'paid' ? 'bg-[#e2f5e9] text-[#22a05b]' : 'bg-yellow-100 text-yellow-700'} px-3 py-1 font-medium capitalize rounded-full text-[13px] border-none`}>
                                    {order.payment_status || 'Pending'}
                                </Badge>
                            </div>
                        </div>
                    </div>

                    {/* Order Summary */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="font-semibold text-[17px] mb-5 text-gray-800">Order Summary</h3>
                        <div className="space-y-3 text-[15px]">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Subtotal:</span>
                                <span className="font-semibold text-gray-800">{formatPrice(order.total)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Tax:</span>
                                <span className="font-semibold text-gray-800">₹0.00</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Shipping:</span>
                                <span className="font-semibold text-gray-800">₹0.00</span>
                            </div>
                            <div className="flex justify-between pt-5 border-t border-gray-100 font-bold text-lg mt-3">
                                <span className="text-gray-800">Total:</span>
                                <span>{formatPrice(order.total)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Payment Details */}
                    {(order.payment_info || order.payment_method !== 'cod') && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                            <h3 className="font-semibold text-[17px] mb-5 text-gray-800">Payment Details</h3>
                            <div className="space-y-3 text-[15px]">
                                {order.payment_info?.payment_id && (
                                    <div className="flex justify-between items-start">
                                        <span className="text-gray-600">Payment ID:</span>
                                        <span className="font-medium text-gray-800 text-right break-all">{order.payment_info.payment_id}</span>
                                    </div>
                                )}
                                <div className="flex justify-between items-start">
                                    <span className="text-gray-600">Transaction ID:</span>
                                    <span className="font-medium text-gray-800 text-right break-all">{order.payment_info?.transaction_id || order.payment_info?.gateway_response?.transactionId || order.payment_id || order.razorpay_order_id || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between items-start">
                                    <span className="text-gray-600">Reference:</span>
                                    <span className="font-medium text-gray-800 text-right break-all">{order.payment_info?.reference_id || order.payment_info?.gateway_response?.referenceId || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between items-start">
                                    <span className="text-gray-600">Provider:</span>
                                    <span className="font-medium text-gray-800 capitalize text-right">{order.payment_info?.gateway_response?.provider || order.payment_method || 'N/A'}</span>
                                </div>
                                {(order.payment_info?.amount || order.payment_info?.gateway_response?.amount) && (
                                    <div className="flex justify-between items-start">
                                        <span className="text-gray-600">Amount:</span>
                                        <span className="font-medium text-gray-800 text-right">{formatPrice(order.payment_info.amount || order.payment_info.gateway_response.amount)}</span>
                                    </div>
                                )}
                                {order.payment_info?.gateway_response?.total && (
                                    <div className="flex justify-between items-start">
                                        <span className="text-gray-600">Settlement Total:</span>
                                        <span className="font-medium text-gray-800 text-right">{formatPrice(order.payment_info.gateway_response.total)}</span>
                                    </div>
                                )}
                                {order.payment_info?.gateway_response?.timestamp && (
                                    <div className="flex justify-between items-start">
                                        <span className="text-gray-600">Date/Time:</span>
                                        <span className="font-medium text-gray-800 text-right">
                                            {formatDate(order.payment_info.gateway_response.timestamp)}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center pt-2">
                                    <span className="text-gray-600">Gateway Status:</span>
                                    <Badge className={`${(order.payment_info?.status?.toLowerCase() === 'success' || order.payment_info?.status?.toLowerCase() === 'paid' || order.payment_info?.gateway_response?.status?.toLowerCase() === 'success' || order.payment_status?.toLowerCase() === 'paid') ? 'bg-[#e2f5e9] text-[#22a05b]' : 'bg-red-100 text-red-700'} px-3 py-1 font-medium capitalize rounded-full text-[13px] border-none`}>
                                        {order.payment_info?.gateway_response?.status || order.payment_info?.status || order.payment_status || 'N/A'}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Update Order Status */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="font-semibold text-[17px] mb-5 text-gray-800">Update Order Status</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Status <span className="text-red-500">*</span></label>
                                <select
                                    className="w-full border border-gray-200 bg-white rounded-lg p-2.5 text-[14px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all cursor-pointer text-gray-700"
                                    value={order.status}
                                    onChange={(e) => setOrder({ ...order, status: e.target.value })}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="shipped">Shipped</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Tracking Number</label>
                                <input
                                    type="text"
                                    placeholder="Enter tracking number"
                                    className="w-full border border-gray-200 bg-[#fafafa] rounded-lg p-2.5 text-[14px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-gray-700 placeholder:text-gray-400"
                                    onChange={(e) => setOrder({ ...order, tracking_number: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Notes</label>
                                <textarea
                                    placeholder="Order notes"
                                    className="w-full border border-gray-200 bg-[#fafafa] rounded-lg p-2.5 text-[14px] h-24 resize-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-gray-700 placeholder:text-gray-400"
                                    onChange={(e) => setOrder({ ...order, notes: e.target.value })}
                                ></textarea>
                            </div>
                            <button
                                onClick={updateOrderStatus}
                                className="w-full bg-[#3b5de7] hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg flex justify-center items-center gap-2 transition-colors mt-2 shadow-sm shadow-blue-600/20 text-[14px]"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                Update Status
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
